import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Link } from "react-router-dom";

export default function WidgetDemo() {
  return (
    <div className="min-h-screen bg-[#171717] text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="md:flex md:justify-between md:items-center md:flex-row flex flex-col-reverse mb-8">
          <div>
            <h1
              className="md:text-3xl text-xl font-bold"
              style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
            >
              Chat Widget Demo
            </h1>
            <p
              className="text-xs mt-2"
              style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
            >
              See the embeddable chat widget in action
            </p>
          </div>
          <Button
            variant="white"
            className="text-xs rounded-full mb-3 md:mb-0"
            style={{ fontFamily: '"Astronomus Regular", sans-serif' }}
            asChild
          >
            <Link to="/support-system/dashboard">← Back to Dashboard</Link>
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 md:gap-8">
          <div className="space-y-4 md:space-y-6">
            <Card className="bg-[#232323] border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle
                  className="text-xs md:text-[12px]"
                  style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                >
                  Widget Features
                </CardTitle>
                <CardDescription
                  className="text-[10px] md:text-xs"
                  style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                >
                  This chat widget can be embedded on any website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4">
                {[
                  "Minimizable and closable interface",
                  "AI-powered automated responses",
                  "File attachment support",
                  "Customizable colors and position",
                  "New message notifications",
                  "Conversation persistence",
                ].map((text, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Badge
                      variant="default"
                      className="text-[8px] md:text-[10px] px-1.5 py-0.5"
                    >
                      ✓
                    </Badge>
                    <span
                      className="text-[9px] md:text-[10px]"
                      style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                    >
                      {text}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-[#232323] border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle
                  className="text-xs md:text-[12px]"
                  style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                >
                  Integration Code
                </CardTitle>
                <CardDescription
                  className="text-[10px] md:text-xs"
                  style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                >
                  Add this code to your website to embed the chat widget
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 text-gray-100 p-2 md:p-4 rounded-lg text-[8px] md:text-[10px] overflow-x-auto">
                  <pre
                    style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                    className="whitespace-pre-wrap break-words"
                  >
                    {`<!-- Add to your HTML head -->
<script src="https://helpapis.ginox.io/widget.js"></script>

<!-- Initialize the widget -->
<script>
  SupportWidget.init({
    apiUrl: 'https://helpapis.ginox.io',
    primaryColor: '#3b82f6',
    position: 'bottom-right',
    welcomeMessage: 'Hi! How can we help you today?'
  });
</script>`}
                  </pre>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#232323] border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle
                  className="text-xs md:text-[12px]"
                  style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                >
                  Customization Options
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 md:space-y-3 text-[9px] md:text-[10px]">
                  <div style={{ fontFamily: '"Stargaze Regular", sans-serif' }}>
                    <strong>apiUrl:</strong> Your support platform URL
                  </div>
                  <div style={{ fontFamily: '"Stargaze Regular", sans-serif' }}>
                    <strong>primaryColor:</strong> Widget theme color (hex code)
                  </div>
                  <div style={{ fontFamily: '"Stargaze Regular", sans-serif' }}>
                    <strong>position:</strong> 'bottom-right' or 'bottom-left'
                  </div>
                  <div style={{ fontFamily: '"Stargaze Regular", sans-serif' }}>
                    <strong>welcomeMessage:</strong> Initial greeting message
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4 md:space-y-6">
            <Card className="bg-[#232323] border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle
                  className="text-xs md:text-[12px]"
                  style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                >
                  Try the Widget
                </CardTitle>
                <CardDescription
                  className="text-[10px] md:text-xs"
                  style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                >
                  Click the chat button in the bottom-right corner to test the
                  widget
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 md:space-y-4">
                  <div className="bg-[#171717] border border-blue-500 rounded-lg p-3 md:p-4">
                    <h4
                      className="font-medium text-blue-400 mb-2 text-[10px] md:text-[12px]"
                      style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                    >
                      Test the Flow:
                    </h4>
                    <ol className="text-[9px] md:text-[10px] text-blue-300 space-y-1">
                      <li
                        style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                      >
                        1. Click the chat button
                      </li>
                      <li
                        style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                      >
                        2. Say "Hello" to start
                      </li>
                      <li
                        style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                      >
                        3. Provide your name when asked
                      </li>
                      <li
                        style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                      >
                        4. Answer if you have an account
                      </li>
                      <li
                        style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                      >
                        5. Ask a question like "How do I reset my password?"
                      </li>
                      <li
                        style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                      >
                        6. See the AI respond from the knowledge base
                      </li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#232323] border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle
                  className="text-xs md:text-[12px]"
                  style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                >
                  Sample Website Content
                </CardTitle>
                <CardDescription
                  className="text-[10px] md:text-xs"
                  style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
                >
                  This simulates how the widget would appear on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 md:space-y-4 text-[9px] md:text-[10px] text-gray-400">
                  <p style={{ fontFamily: '"Stargaze Regular", sans-serif' }}>
                    Welcome to our amazing product! We're here to help you
                    succeed with our platform.
                  </p>
                  <p style={{ fontFamily: '"Stargaze Regular", sans-serif' }}>
                    Our customer support team is available 24/7 through our
                    AI-powered chat system. Get instant answers to common
                    questions or connect with a human agent when needed.
                  </p>
                  <p style={{ fontFamily: '"Stargaze Regular", sans-serif' }}>
                    Try clicking the chat widget in the bottom-right corner to
                    experience our automated support flow. The AI will guide you
                    through collecting your information and provide relevant
                    answers from our knowledge base.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
